<template>
  <div class="log-query-container">
    <div class="title">
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>平台建设</el-breadcrumb-item>
        <el-breadcrumb-item>服务中心</el-breadcrumb-item>
        <el-breadcrumb-item>日志查询</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-card class="search-card">
      <div class="search-row">
        <div class="search-item">
          <div class="label">日志文件名</div>
          <el-select
            v-model="query.file_name"
            filterable
            placeholder="请选择日志文件"
            class="file-select"
            @change="handleFileChange"
          >
            <el-option
              v-for="item in fileOptions"
              :key="item.file_name"
              :label="`${item.file_name} (${item.modified_time})`"
              :value="item.file_name"
            />
          </el-select>
        </div>
        <div class="search-item">
          <div class="label">全文搜索</div>
          <el-input
            v-model="query.keyword"
            clearable
            placeholder="输入关键字进行全文搜索"
            class="keyword-input"
            @keyup.enter.native="loadLogContent"
          />
        </div>
        <div class="search-buttons">
          <el-button type="primary" :loading="loading" @click="loadLogContent">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </div>
      </div>
      <div class="summary" v-if="summary.file_name">
        当前文件：{{ summary.file_name }} | 总行数：{{ summary.total_lines }} | 匹配行数：{{ summary.matched_lines }}
      </div>
    </el-card>

    <el-card class="viewer-card" v-loading="loading">
      <div class="viewer-title">日志查阅器（倒序：最新在上）</div>
      <el-input
        class="log-viewer-textarea"
        type="textarea"
        :rows="26"
        resize="none"
        readonly
        :value="logContent"
        placeholder="请先选择日志文件"
      />
    </el-card>
  </div>
</template>

<script>
import { getLogContent, getLogFiles } from '@/api/logQuery';

export default {
  name: 'LogQuery',
  data() {
    return {
      loading: false,
      fileOptions: [],
      query: {
        file_name: '',
        keyword: '',
      },
      summary: {
        file_name: '',
        total_lines: 0,
        matched_lines: 0,
      },
      logContent: '',
    };
  },
  async created() {
    await this.loadLogFiles();
  },
  methods: {
    getPayloadData(res) {
      return (res && res.data && res.data.data) || {};
    },
    async loadLogFiles() {
      this.loading = true;
      try {
        const res = await getLogFiles();
        const data = this.getPayloadData(res);
        this.fileOptions = data.files || [];
        if (this.fileOptions.length) {
          this.query.file_name = this.fileOptions[0].file_name;
          await this.loadLogContent();
        }
      } catch (error) {
        console.error('获取日志文件列表失败:', error);
        this.$message.error('获取日志文件列表失败');
      } finally {
        this.loading = false;
      }
    },
    async loadLogContent() {
      if (!this.query.file_name) {
        this.$message.warning('请先选择日志文件');
        return;
      }
      this.loading = true;
      try {
        const res = await getLogContent({
          file_name: this.query.file_name,
          keyword: this.query.keyword || '',
        });
        const data = this.getPayloadData(res);
        this.summary = {
          file_name: data.file_name || '',
          total_lines: data.total_lines || 0,
          matched_lines: data.matched_lines || 0,
        };
        this.logContent = data.content || '';
      } catch (error) {
        console.error('获取日志内容失败:', error);
        const detail = error && error.response && error.response.data && error.response.data.detail;
        this.$message.error(detail ? `获取日志内容失败：${detail}` : '获取日志内容失败');
      } finally {
        this.loading = false;
      }
    },
    async handleFileChange() {
      await this.loadLogContent();
    },
    async resetSearch() {
      this.query.keyword = '';
      await this.loadLogContent();
    },
  },
};
</script>

<style lang="scss" scoped>
.log-query-container {
  padding: 20px;
  min-height: calc(100vh - 84px);
  background: #f5f7fb;
}

.title {
  margin-bottom: 16px;
}

.search-card {
  margin-bottom: 16px;
}

.search-row {
  display: flex;
  align-items: flex-end;
  gap: 12px;
  flex-wrap: wrap;
}

.search-item {
  display: flex;
  flex-direction: column;
}

.label {
  font-size: 12px;
  color: #6b778c;
  margin-bottom: 6px;
}

.file-select {
  width: 420px;
}

.keyword-input {
  width: 320px;
}

.search-buttons {
  display: flex;
  gap: 8px;
}

.summary {
  margin-top: 10px;
  color: #4a5872;
  font-size: 13px;
}

.viewer-card {
  .viewer-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2d3d;
    margin-bottom: 10px;
  }
}

.log-viewer-textarea ::v-deep .el-textarea__inner {
  font-family: Consolas, Menlo, Monaco, monospace;
  font-size: 12px;
  line-height: 1.5;
  /* Firefox：更粗、更明显 */
  scrollbar-width: auto;
  scrollbar-color: #6b7a99 #e8ecf4;
}

/* WebKit / Chromium：加粗滚动条轨道与滑块 */
.log-viewer-textarea ::v-deep .el-textarea__inner::-webkit-scrollbar {
  width: 14px;
  height: 14px;
}

.log-viewer-textarea ::v-deep .el-textarea__inner::-webkit-scrollbar-track {
  background: #e8ecf4;
  border-radius: 8px;
  border: 1px solid #d5dbe8;
}

.log-viewer-textarea ::v-deep .el-textarea__inner::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #7a8aad 0%, #5c6b8a 100%);
  border-radius: 8px;
  border: 2px solid #e8ecf4;
  min-height: 48px;
}

.log-viewer-textarea ::v-deep .el-textarea__inner::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(180deg, #5c6b8a 0%, #4a5872 100%);
}
</style>
